import { Lock } from 'lucide-react'
import { cn } from '../lib/cn'

/**
 * The visual side of staging a demo sprint by sprint.
 *
 * <p>Everything here takes what it needs as props. This package is
 * presentational and does not depend on `@waago/api`, which is where the
 * sprint registry and the build's own sprint number live — a badge component
 * that reaches into application state to decide whether to render is a
 * component you cannot put in a story or a test without booting the app.
 *
 * <p>So the apps decide, and these draw. See `sprint.ts` in `@waago/api` for
 * the decision, and each app's `SprintGate` for the wiring.
 */

/** 1–4. Kept as a plain number so this file needs no import to state it. */
export type SprintNumber = 1 | 2 | 3 | 4

/**
 * The marker on something this build is not showing yet.
 *
 * <p>Small, quiet and next to the thing it refers to. It names the sprint
 * rather than saying "bientôt", because a commitment the client can hold you
 * to is worth more to them than a vague one, and the roadmap already makes it.
 */
export function LockBadge({ sprint, className }: { sprint: SprintNumber; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5',
        // slate-500 on slate-100 is 6.3:1 — a pending state is still text
        // somebody has to read.
        'text-2xs font-medium text-slate-500',
        className,
      )}
    >
      <Lock className="size-3" aria-hidden="true" />
      Sprint {sprint}
    </span>
  )
}

/**
 * A whole screen that is not in this build yet.
 *
 * <p>It explains itself rather than 404ing. Someone who follows a link from a
 * list into a locked detail screen has not made a mistake — the list was
 * deliberately left visible — so this says what lands here, when, and offers
 * the way back.
 *
 * <p>`role="status"`, not `alert`: this is information, not a problem.
 */
export function LockedScreen({
  sprint,
  days,
  scope,
  currentSprint,
  title,
  onBack,
}: {
  sprint: SprintNumber
  /** "Jours 21–30", from the roadmap. */
  days: string
  /** What that sprint delivers, in one line. */
  scope: string
  /** What this build is showing, for the explanatory paragraph. */
  currentSprint: SprintNumber
  /** What would be here. Given so the screen is about the feature, not the lock. */
  title?: string
  onBack?: () => void
}) {
  return (
    <div
      role="status"
      className="animate-slide-up flex flex-col items-center gap-4 px-6 py-16 text-center"
    >
      <span className="grid size-16 place-items-center rounded-full bg-slate-100">
        <Lock className="size-7 text-slate-400" aria-hidden="true" />
      </span>

      <div className="flex flex-col gap-1">
        <h1 className="text-lg font-bold text-slate-900">
          {title ?? 'Prévu pour un prochain sprint'}
        </h1>
        <p className="text-brand-700 text-sm font-medium">
          Sprint {sprint} · {days}
        </p>
      </div>

      <p className="max-w-xs text-sm leading-5 text-slate-600">{scope}</p>

      <p className="text-2xs max-w-xs leading-4 text-slate-500">
        Cette version présente le sprint {currentSprint}. Les fonctionnalités des sprints suivants
        sont livrées au fur et à mesure du calendrier.
      </p>

      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-brand-700 press mt-2 text-sm underline"
        >
          Retour
        </button>
      ) : null}
    </div>
  )
}

/**
 * The build's own label, so nobody has to guess which sprint is on screen.
 * Render it only while something is actually held back.
 */
export function SprintRibbon({
  sprint,
  days,
  className,
}: {
  sprint: SprintNumber
  days: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center gap-2 px-3 py-1',
        'bg-brand-50 text-brand-800 text-2xs font-medium',
        className,
      )}
    >
      <span className="font-semibold">Sprint {sprint}</span>
      <span aria-hidden="true">·</span>
      <span className="truncate">{days}</span>
    </div>
  )
}
